/** Shared helpers for Sell Timber (geo keys, ids, sv-SE display). */

import format from 'date-fns/format';
import sv from 'date-fns/locale/sv';

import { SELL_TIMBER_GROT_MARKER_ICON_KEY } from './sellTimberMarkerIcons';

/**
 * `value` matches `Ad.arrangement` (backend) — labels are UI-only.
 * `shortLabel` = kort namn (t.ex. auto-genererad annonskommentar).
 * @type {ReadonlyArray<{ value: string, label: string, shortLabel: string }>}
 */
export const ACTION_OPTIONS = [
  {
    value: 'slutavverkning',
    label: 'Slutavverkning (Hugga färdigväxt skog)',
    shortLabel: 'Slutavverkning'
  },
  {
    value: 'gallring',
    label: 'Gallring (Ta bort svaga träd)',
    shortLabel: 'Gallring'
  },
  {
    value: 'plockhuggning',
    label: 'Plockhuggning (Enskilda träd)',
    shortLabel: 'Plockhuggning'
  },
  {
    value: 'grot',
    label: 'Biobränsle (GROT)',
    shortLabel: 'att ta ut biobränsle'
  },
  {
    value: 'vindfallen',
    label: 'Övrig avverkning (Oförutsedda insatser i skogen)',
    shortLabel: 'Övrig avverkning'
  }
];

/**
 * `value` matches auction type `label` from GET /auctions-api/auctionstype/.
 * @type {ReadonlyArray<{ value: string, label: string }>}
 */
export const SALE_OPTIONS = [
  { value: 'avverkningsuppdrag', label: 'Avverkningsuppdrag (Prislista minus faktisk kostnad)' },
  { value: 'leveransrotköp', label: 'Leveransrotköp (Fast nettopris per kubik)' },
  { value: 'leveransvirke', label: 'Leveransvirke (Egen avverkning till bilväg)' },
  { value: 'rotpost', label: 'Rotpost (Sälja stående inmätt skog för fastpris)' }
];

/** Default wizard `selectedSale` — keep in sync with first {@link SALE_OPTIONS} entry. */
export const SELL_TIMBER_DEFAULT_SELECTED_SALE = SALE_OPTIONS[0]?.value ?? 'avverkningsuppdrag';

/** `Ad.arrangement` / wizard `selectedAction` for biobränsle-only ads. */
export const GROT_ARRANGEMENT = 'grot';

/** AdType.name / auctions-api auctionstype `label` for biobränsle ads (same string, different FK). */
export const SELL_TIMBER_GROT_AD_TYPE = GROT_ARRANGEMENT;

/**
 * @param {string | { is_grot?: boolean, arrangement_key?: string, selectedAction?: string } | null | undefined} value
 */
export function isGrotArrangement(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value === GROT_ARRANGEMENT;
  return (
    value.is_grot === true ||
    value.arrangement_key === GROT_ARRANGEMENT ||
    value.selectedAction === GROT_ARRANGEMENT
  );
}

/** Persisted on placed marker `properties.icon` for grot flow. */
export const SELL_TIMBER_GROT_MARKER_ICON = SELL_TIMBER_GROT_MARKER_ICON_KEY;

/** Label shown on map and in summaries for the grot location marker. */
export const SELL_TIMBER_GROT_MARKER_NAME = 'Biobränsle';

/**
 * @param {Array<{ properties?: { icon?: string, name?: string } }>} markers
 * @returns {Array<object>}
 */
export function filterGrotBiobransleMarkers(markers) {
  if (!Array.isArray(markers)) return [];
  const match = markers.filter(
    (f) =>
      f?.properties?.icon === SELL_TIMBER_GROT_MARKER_ICON ||
      f?.properties?.name === SELL_TIMBER_GROT_MARKER_NAME
  );
  return match;
}

/** Standard Sell Timber map: hide biobränsle markers (grot-only). */
export function excludeGrotMarkerFromTimberPlacedMarkers(markers) {
  if (!Array.isArray(markers)) return [];
  return markers.filter((f) => f?.properties?.icon !== SELL_TIMBER_GROT_MARKER_ICON);
}

/**
 * @param {object | null | undefined} geometry
 */
export function hasGrotBiobransleMarkerPlacement(geometry) {
  if (!geometry || typeof geometry !== 'object') return false;
  const markers = geometry.placedMarkers;
  if (!Array.isArray(markers)) return false;
  return filterGrotBiobransleMarkers(markers).length > 0;
}

/** Wizard / radio values for grot step 3 yes–no questions. */
export const SELL_TIMBER_GROT_JA_NEJ = {
  JA: 'ja',
  NEJ: 'nej'
};

export const SELL_TIMBER_GROT_JA_NEJ_OPTIONS = [
  { value: SELL_TIMBER_GROT_JA_NEJ.JA, label: 'Ja' },
  { value: SELL_TIMBER_GROT_JA_NEJ['NEJ'], label: 'Nej' }
];

/**
 * @param {{ grot_volume?: string, grot_heap_covered?: string, grot_collected?: string }} fields
 * @returns {string[]} Swedish validation messages
 */
export function grotStep3ValidationMessages(fields) {
  const missing = [];
  const vol = fields?.grot_volume != null ? String(fields.grot_volume).trim() : '';
  if (!vol) {
    missing.push('ange hur många kubik det är');
  } else if (!Number.isFinite(Number(vol)) || Number(vol) <= 0) {
    missing.push('ange en giltig volym (kubik) större än noll');
  }
  const heap = normalizeGrotJaNejForApi(fields?.grot_heap_covered);
  if (!heap) {
    missing.push('svara om högen är täckt');
  }
  const collected = normalizeGrotJaNejForApi(fields?.grot_collected);
  if (!collected) {
    missing.push('svara om biobränslet är uppsamlat');
  }
  return missing;
}

/** @param {string | undefined} value */
export function labelGrotJaNej(value) {
  const v = value != null ? String(value).trim().toLowerCase() : '';
  if (v === SELL_TIMBER_GROT_JA_NEJ.JA) return 'Ja';
  if (v === SELL_TIMBER_GROT_JA_NEJ['NEJ']) return 'Nej';
  return '-';
}

/** @param {string | undefined} value — API/storage value `ja` / `nej`, or null if invalid */
export function normalizeGrotJaNejForApi(value) {
  const v = value != null ? String(value).trim().toLowerCase() : '';
  if (v === SELL_TIMBER_GROT_JA_NEJ.JA) return SELL_TIMBER_GROT_JA_NEJ.JA;
  if (v === SELL_TIMBER_GROT_JA_NEJ['NEJ']) return SELL_TIMBER_GROT_JA_NEJ['NEJ'];
  return null;
}

/**
 * Wizard `grotCommentsExtra` → API `comments` (optional text only; step 3 uses model fields).
 * Returns `null` when empty so PATCH/POST can omit or clear the field.
 * @param {object | null | undefined} adCreateData
 */
export function grotCommentsForApi(adCreateData) {
  if (adCreateData?.grotCommentsExtra == null) return null;
  const text = String(adCreateData.grotCommentsExtra).trim();
  return text || null;
}

/**
 * @param {object | null | undefined} adCreateData
 * @param {{ customArea: object, comments?: string | null }} opts
 */
export function buildGrotFinalizePatch(adCreateData, { customArea, comments }) {
  return {
    arrangement: GROT_ARRANGEMENT,
    grot_volume: String(adCreateData?.grot_volume ?? '').trim(),
    grot_heap_covered: normalizeGrotJaNejForApi(adCreateData?.grot_heap_covered),
    grot_collected: normalizeGrotJaNejForApi(adCreateData?.grot_collected),
    comments: comments ?? grotCommentsForApi(adCreateData),
    custom_area: customArea,
    crs: 4326,
    state: 20
  };
}

/**
 * @param {Array<{ existing?: boolean, file?: File }>} attachments
 */
export function grotHasAtLeastOneAttachment(attachments) {
  if (!Array.isArray(attachments) || !attachments.length) return false;
  return attachments.some((a) => a?.existing || a?.file instanceof File);
}

/** User-facing copy for any server/API failure in this flow (never raw response bodies). */
export const SELL_TIMBER_GENERIC_ERROR = 'Ett fel uppstod.';

const SV_NUMBER = 'sv-SE';

/** Stable id for avverkningsanmälan selection; matches FinalfellingApplication serialization. */
export function fellingApplicationKeyFromFeature(feature, index) {
  const p = feature?.properties;
  if (p == null) return `felling-${index}`;
  if (p.application_id != null && String(p.application_id) !== '') {
    return String(p.application_id);
  }
  if (p.id != null) return String(p.id);
  return `felling-${index}`;
}

/**
 * Next table row id `#1`, `#2`, … from existing rows (only `#` + digits participate).
 * @param {Array<{ id?: unknown }>} sections
 */
export function nextSellTimberSectionId(sections) {
  const max = sections.reduce((acc, row) => {
    const id = row?.id;
    if (typeof id !== 'string') return acc;
    const m = id.match(/^#(\d+)$/);
    return m ? Math.max(acc, Number(m[1])) : acc;
  }, 0);
  return `#${max + 1}`;
}

/**
 * Round areal (ha) to one decimal.
 * @param {unknown} value
 * @param {{
 *   invalidAsZero?: boolean,
 *   invalidValue?: unknown,
 *   invalidNumberAsOriginal?: boolean
 * }} [options]
 *   - `invalidAsZero: true` — treat empty / invalid parses like Step 3 shape fetch (see below).
 *   - `invalidValue` — when `invalidAsZero` and value is `''` or `null`/`undefined`, return this instead of `0` (does not apply to NaN parses).
 *   - `invalidNumberAsOriginal` — when `invalidAsZero` and `Number(value)` is NaN, return `String(value)` instead of `0`.
 *   - default (no `invalidAsZero`) — invalid number kept as string (legacy `mapForestSectionToRow`).
 * @returns {number|string}
 */
export function roundHa(value, options = {}) {
  const invalidAsZero = options.invalidAsZero === true;
  const invalidValue = options.invalidValue;
  const invalidNumberAsOriginal = options.invalidNumberAsOriginal === true;

  if (invalidAsZero && (value === '' || value == null)) {
    return invalidValue !== undefined ? invalidValue : 0;
  }

  const n = Number(value);
  if (Number.isNaN(n)) {
    if (!invalidAsZero) return String(value);
    if (invalidNumberAsOriginal) return String(value);
    return 0;
  }

  return Math.round(n * 10) / 10;
}

/**
 * Single entry point for Sell Timber date strings (API ISO/datetime, YYYY-MM-DD, etc.).
 *
 * @param {unknown} value
 * @param {{ style: 'ymd' } | { style: 'svLong', emptyLabel?: string }} options
 *   - `ymd` — normalize to `YYYY-MM-DD` (empty string if missing/invalid).
 *   - `svLong` — Swedish long date (e.g. "7 april 2026"); uses a leading `YYYY-MM-DD` as local calendar
 *     date when present, otherwise `Date` parse. `emptyLabel` defaults to `—`.
 * @returns {string}
 */
export function formatSellTimberDate(value, options) {
  const isYmd = options.style === 'ymd';
  const emptyLabel = options.emptyLabel ?? '—';

  if (isYmd && (value == null || value === '')) return '';
  if (!isYmd && (value == null || String(value).trim() === '')) return emptyLabel;

  const s = isYmd ? String(value) : String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isYmd && m) return m[0];

  const d =
    !isYmd && m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(s);

  if (Number.isNaN(d.getTime())) return isYmd ? '' : emptyLabel;
  return isYmd ? format(d, 'yyyy-MM-dd') : format(d, 'd MMMM y', { locale: sv });
}

/**
 * Locale-formatted number for sv-SE (grouping, decimal comma). Rounds to `maximumFractionDigits` first.
 *
 * @param {unknown} value
 * @param {{
 *   maximumFractionDigits?: number,
 *   emptyLabel?: string,
 *   suffix?: string,
 *   invalidAsEmpty?: boolean
 * }} [options]
 *   - `suffix` — appended after the formatted number (e.g. `'%'`).
 *   - `invalidAsEmpty` — if true, `NaN` yields `emptyLabel` instead of the raw string (used for TGL %).
 */
export function formatSvLocaleNumber(value, options = {}) {
  const emptyLabel = options.emptyLabel ?? '—';
  const suffix = options.suffix ?? '';
  const invalidAsEmpty = options.invalidAsEmpty === true;
  const maxFrac = options.maximumFractionDigits ?? 0;

  if (value === '' || value == null) return emptyLabel;
  const n = Number(value);
  if (Number.isNaN(n)) return invalidAsEmpty ? emptyLabel : String(value);

  const factor = 10 ** maxFrac;
  const rounded = Math.round(n * factor) / factor;
  const formatted = rounded.toLocaleString(SV_NUMBER, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFrac
  });
  return suffix ? `${formatted}${suffix}` : formatted;
}

/**
 * Best-effort kommun name from fastighetsbeteckning (same idea as woodprices in PropSelector).
 * @param {string | undefined | null} designation
 * @returns {string}
 */
export function municipalityFromPropertyDesignation(designation) {
  const s = (designation ?? '').trim();
  if (!s) return '';
  const muniNames = s.split(/\s+/).filter(Boolean);
  if (!muniNames.length) return '';
  const compoundPrefixes = ['Upplands', 'Lilla', 'Östra', 'Västra', 'Södra', 'Norra'];
  let first = muniNames[0][0] + muniNames[0].slice(1).toLowerCase();
  if (compoundPrefixes.includes(first) && muniNames[1]) {
    const second = muniNames[1][0] + muniNames[1].slice(1).toLowerCase();
    return `${first} ${second}`;
  }
  return first;
}

/**
 * TGL-trädslag som steg 3-tabellen / API använder (samma ordning som kolumnerna).
 * @type {ReadonlyArray<{ key: string, label: string }>}
 */
export const SELL_TIMBER_SPECIES_TGL = [
  { key: 'tall', label: 'Tall' },
  { key: 'gran', label: 'Gran' },
  { key: 'lov', label: 'Löv' },
  { key: 'al', label: 'Al' },
  { key: 'asp', label: 'Asp' },
  { key: 'björk', label: 'Björk' },
  { key: 'bok', label: 'Bok' },
  { key: 'ek', label: 'Ek' }
];

const SELL_TIMBER_PRIMARY_TGL_KEYS = new Set(['tall', 'gran', 'lov']);

/**
 * Lövträd m.m. (inte tall/gran/löv) — samma ordning som underlistan i {@link SELL_TIMBER_SPECIES_TGL}.
 * @type {ReadonlyArray<{ key: string, label: string }>}
 */
export const SELL_TIMBER_EXTRA_SPECIES = SELL_TIMBER_SPECIES_TGL.filter(
  ({ key }) => !SELL_TIMBER_PRIMARY_TGL_KEYS.has(key)
);

/**
 * Virkesrad lagrar TGL som 0–100 (heltal); omvandlar till andel av radvolym för m³sk per trädslag.
 * @param {unknown} pct
 * @returns {number}
 */
export function sellTimberTglPctToVolumeFraction(pct) {
  const n = Number(pct);
  if (Number.isNaN(n) || n <= 0) return 0;
  return n > 1 ? n / 100 : n;
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {number | null}
 */
export function sellTimberRowAreaHaOrNull(row) {
  if (!row || row.area === '' || row.area == null) return null;
  const a = Number(row.area);
  return Number.isNaN(a) ? null : a;
}

/**
 * Areal (ha) från fastighetens SBP-avdelning i kart-state (`geometry.sections`).
 * @param {object | null | undefined} geometry
 * @param {unknown} localID
 * @returns {number | null}
 */
export function sellTimberSectionBundleAreaHaOrNull(geometry, localID) {
  const sect = (geometry?.sections ?? []).find((s) => String(s.localID) === String(localID));
  if (!sect || sect.area == null) return null;
  const a = Number(sect.area);
  return Number.isNaN(a) ? null : a;
}

/**
 * @param {Array<Record<string, unknown>>} rows
 * @returns {number}
 */
export function sumSellTimberRowsVolumeM3sk(rows) {
  return rows.reduce((acc, row) => {
    const v = Number(row.volumeM3sk);
    return acc + (Number.isNaN(v) ? 0 : v);
  }, 0);
}

/**
 * Summerar m³sk per trädslag från virkesrader (TGL × volym per rad).
 * @param {Array<Record<string, unknown>>} rows
 * @returns {Array<{ label: string, volume: number }>}
 */
export function aggregateSellTimberSpeciesVolumesM3sk(rows) {
  /** @type {Record<string, number>} */
  const totals = Object.fromEntries(SELL_TIMBER_SPECIES_TGL.map(({ key }) => [key, 0]));
  for (const row of rows) {
    const volSk = Number(row.volumeM3sk);
    if (Number.isNaN(volSk) || volSk <= 0) continue;
    for (const { key } of SELL_TIMBER_SPECIES_TGL) {
      const frac = sellTimberTglPctToVolumeFraction(row[key]);
      if (frac <= 0) continue;
      totals[key] += volSk * frac;
    }
  }
  return SELL_TIMBER_SPECIES_TGL.map(({ key, label }) => ({
    label,
    volume: totals[key]
  }))
    .filter(({ volume }) => volume >= 0.5)
    .sort((a, b) => b.volume - a.volume);
}

function sellTimberActionShortLabel(selectedAction) {
  if (selectedAction == null || String(selectedAction).trim() === '') return '—';
  const o = ACTION_OPTIONS.find((x) => x.value === selectedAction);
  return o?.shortLabel?.trim() || '—';
}

/**
 * Auto-generated annonskommentar (steg 4). Pass `features` from
 * `collectFeaturesFromMapSelection(geometry)` so this module need not import `sellTimberMapSelection` (would cycle).
 *
 * @param {{
 *   designation?: string | null,
 *   selectedAction: string | null | undefined,
 *   geometry: object | null | undefined,
 *   features: unknown[],
 *   virkesRows: Array<Record<string, unknown>>
 * }} params
 * @returns {string}
 */
export function buildSellTimberSuggestedComment({
  designation,
  selectedAction,
  geometry,
  features,
  virkesRows
}) {
  const list = Array.isArray(features) ? features : [];
  const n = list.length;
  const rows = Array.isArray(virkesRows) ? virkesRows : [];

  const municipality = municipalityFromPropertyDesignation(designation ?? '');
  const muniPart = municipality ? `${municipality} kommun` : 'kommunen';
  const typePart = sellTimberActionShortLabel(selectedAction);

  if (n === 0) {
    return `Jag bjuder ut virke i ${muniPart} för ${typePart}. Välkommen att lämna anbud.`;
  }

  const avd = n === 1 ? 'en avdelning' : `${n} avdelningar`;

  let totalHa = 0;
  for (let i = 0; i < n; i++) {
    const row = rows[i];
    let ha = sellTimberRowAreaHaOrNull(row);
    if (ha == null) {
      const feat = list[i];
      if (feat?.properties?.source === 'section' && feat.properties.localID != null) {
        ha = sellTimberSectionBundleAreaHaOrNull(geometry, feat.properties.localID);
      }
    }
    if (ha != null) totalHa += ha;
  }
  totalHa = Math.round(totalHa * 10) / 10;

  const alignedRows = rows.slice(0, n);
  const totalVol = sumSellTimberRowsVolumeM3sk(alignedRows);
  const speciesList = aggregateSellTimberSpeciesVolumesM3sk(alignedRows);

  const haStr = formatSvLocaleNumber(totalHa, { maximumFractionDigits: 1, emptyLabel: '0' });
  const volStr = formatSvLocaleNumber(Math.round(totalVol), { maximumFractionDigits: 0, emptyLabel: '0' });

  const speciesStr = speciesList.length
    ? speciesList
        .map(
          ({ label, volume }) =>
            `${label} (${formatSvLocaleNumber(Math.round(volume), { maximumFractionDigits: 0, emptyLabel: '0' })} m³sk)`
        )
        .join(', ')
    : '—';

  return `Jag bjuder ut ${avd} i ${muniPart} på min fastighet ${designation.toUpperCase()} för ${typePart}. Totalt ${haStr} ha med ${volStr} m³sk fördelat på ${speciesStr}. Välkommen att lämna anbud.`;
}

/**
 * Auto-generated annonskommentar för biobränsle (grot), steg 4.
 *
 * @param {{
 *   designation?: string | null,
 *   geometry?: object | null,
 *   grot_volume?: string | null,
 *   grot_heap_covered?: string | null,
 *   grot_collected?: string | null
 * }} params
 * @returns {string}
 */
export function buildGrotSuggestedComment({
  designation,
  geometry,
  grot_volume,
  grot_heap_covered,
  grot_collected
}) {
  const municipality = municipalityFromPropertyDesignation(designation ?? '');
  const muniPart = municipality ? `${municipality} kommun` : 'kommunen';
  const des = (designation ?? '').trim();
  const propertyClause = des ? ` på fastigheten ${des.toUpperCase()}` : '';

  const markerCount = filterGrotBiobransleMarkers(geometry?.placedMarkers ?? []).length;
  const volRaw = grot_volume != null ? String(grot_volume).trim() : '';

  let text = `Jag bjuder ut biobränsle i ${muniPart}${propertyClause}.`;

  if (volRaw) {
    text += ` Uppskattad volym cirka ${volRaw} m³.`;
  }

  const heap = labelGrotJaNej(grot_heap_covered);
  if (heap === 'Ja') {
    text += ' Högen är täckt.';
  } else if (heap === 'Nej') {
    text += ' Högen är inte täckt.';
  }

  const collected = labelGrotJaNej(grot_collected);
  if (collected === 'Ja') {
    text += ' Biobränslet är uppsamlat.';
  } else if (collected === 'Nej') {
    text += ' Biobränslet är inte uppsamlat.';
  }

  if (markerCount === 1) {
    text += ' Platsen är markerad på kartan i annonsen.';
  } else if (markerCount > 1) {
    text += ` ${markerCount} platser är markerade på kartan i annonsen.`;
  }

  text += ' Välkommen att lämna anbud.';
  return text;
}
